import { Image, File, Code, Frame, FileImage, Video, Music, Type, FileJson, Box } from 'lucide-react';
import { PromptItemType } from './prompt-input/PromptItemNode';

interface ParsedPromptItem {
  type: PromptItemType;
  id: string;
  label: string;
}

/**
 * Parse prompt item markers from text
 * Format: __PROMPT_ITEM[type:element|id:edit-xxx|label:Game Title]__
 */
function parsePromptItems(text: string): (string | ParsedPromptItem)[] {
  const parts: (string | ParsedPromptItem)[] = [];
  const regex = /__PROMPT_ITEM\[((?:[^\]\\]|\\.)*)\]__/g;
  let lastIndex = 0;
  let match;

  while ((match = regex.exec(text)) !== null) {
    // Add text before the marker
    if (match.index > lastIndex) {
      parts.push(text.substring(lastIndex, match.index));
    }

    // Parse the marker content
    const content = match[1];
    const props: Record<string, string> = {};
    
    // Split by unescaped pipes
    const segments = content.split(/(?<!\\)\|/);
    segments.forEach(segment => {
      const [key, ...valueParts] = segment.split(':');
      const value = valueParts.join(':').replace(/\\\|/g, '|').replace(/\\\]/g, ']');
      props[key] = value;
    });

    if (props.type && props.id && props.label) {
      parts.push({
        type: props.type as PromptItemType,
        id: props.id,
        label: props.label,
      });
    }

    lastIndex = regex.lastIndex;
  }

  // Add remaining text
  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }

  return parts;
}

/**
 * PromptItemTag - Visual representation of a prompt item
 */
function PromptItemTag({ item }: { item: ParsedPromptItem }) {
  const getIcon = () => {
    switch (item.type) {
      case 'image':
        return <Image className="w-3 h-3" />;
      case 'document':
        return <File className="w-3 h-3" />;
      case 'code':
        return <Code className="w-3 h-3" />;
      case 'element':
        return <Frame className="w-3 h-3" />;
      case 'asset':
        return <FileImage className="w-3 h-3" />;
      case 'asset-image':
        return <Image className="w-3 h-3" />;
      case 'asset-video':
        return <Video className="w-3 h-3" />;
      case 'asset-audio':
        return <Music className="w-3 h-3" />;
      case 'asset-font':
        return <Type className="w-3 h-3" />;
      case 'asset-data':
        return <FileJson className="w-3 h-3" />;
      case 'asset-3d':
        return <Box className="w-3 h-3" />;
      default:
        return <File className="w-3 h-3" />;
    }
  };

  const getColorClasses = () => {
    switch (item.type) {
      case 'image':
        return "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-300 dark:border-blue-700";
      case 'document':
        return "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 border-green-300 dark:border-green-700";
      case 'code':
        return "bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 border-purple-300 dark:border-purple-700";
      case 'element':
        return "bg-pink-100 dark:bg-pink-900/30 text-pink-700 dark:text-pink-300 border-pink-300 dark:border-pink-700";
      case 'asset':
        return "bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 border-amber-300 dark:border-amber-700";
      // Asset type-specific colors (matching PromptItemNode)
      case 'asset-image':
        return "bg-sky-100 dark:bg-sky-900/30 text-sky-700 dark:text-sky-300 border-sky-300 dark:border-sky-700";
      case 'asset-video':
        return "bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300 border-violet-300 dark:border-violet-700";
      case 'asset-audio':
        return "bg-rose-100 dark:bg-rose-900/30 text-rose-700 dark:text-rose-300 border-rose-300 dark:border-rose-700";
      case 'asset-font':
        return "bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 border-indigo-300 dark:border-indigo-700";
      case 'asset-data':
        return "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 border-emerald-300 dark:border-emerald-700";
      case 'asset-3d':
        return "bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 border-orange-300 dark:border-orange-700";
      default:
        return "bg-gray-100 dark:bg-gray-900/30 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-700";
    }
  };

  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium border ${getColorClasses()}`}
      style={{ userSelect: 'none' }}
    >
      {getIcon()}
      <span>{item.label}</span>
    </span>
  );
}

/**
 * PromptItemRenderer - Renders text with prompt item markers as visual tags
 */
interface PromptItemRendererProps {
  text: string;
  className?: string;
}

export default function PromptItemRenderer({ text, className }: PromptItemRendererProps) {
  const parts = parsePromptItems(text);

  return (
    <span className={className}>
      {parts.map((part, index) => {
        if (typeof part === 'string') {
          return <span key={index}>{part}</span>;
        } else {
          return <PromptItemTag key={index} item={part} />;
        }
      })}
    </span>
  );
}

